/**
 * Mobile Style Service
 *
 * SINGLE source of truth for ALL mobile UI styling.
 * Do NOT hardcode colors, backgrounds, borders, or gradients in mobile components.
 * Import from this file instead.
 *
 * Desktop styling lives in colorService.js — do NOT use this file for desktop components.
 */

// ---------------------------------------------------------------------------
// 1. Page Background
// ---------------------------------------------------------------------------
export const mobilePageBackground = 'linear-gradient(180deg, #16161e 0%, #111118 50%, #0d0d14 100%)';
export const mobilePageBackgroundFlat = '#111118'; // Flat fallback for selects, sticky cells, etc.

// ---------------------------------------------------------------------------
// 2. Tile Style (Style C)
// ---------------------------------------------------------------------------
export const mobileTileStyle = {
  background: 'linear-gradient(180deg, #252530 0%, #1c1c22 50%, #1a1a1e 100%)',
  border: '1px solid #444',
  borderRadius: '10px',
  boxShadow: 'inset 0 0 0 1px rgba(234, 179, 8, 0.12)',
};

// ---------------------------------------------------------------------------
// 3. Table Styles
// ---------------------------------------------------------------------------
export const mobileTableStyles = {
  header: {
    background: '#252530',
    border: '1px solid #444',
  },
  // Modal table column-header TYPOGRAPHY (single source for Game Logs + the
  // Stats StatBlocks). Legible-not-heavy: 10px / weight 600 / uppercase with a
  // little letter-spacing so the caps breathe (was 8px / 800, cramped).
  headerText: {
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  // House table shading (HANDOFF global rule 6 / CLAUDE.md RosterTable):
  // per-group zebra — odd #0d0d14 / even #181820. The sticky POS/Player column
  // uses the EXACT same solid as its row (sticky.even/odd mirror rows.even/odd),
  // never a different shade — CSS can't do rgba on a sticky cell, so these must
  // be opaque, and they are byte-identical to the scrolling rows.
  rows: {
    even: '#181820',
    odd:  '#0d0d14',
    border: '1px solid rgba(255, 255, 255, 0.05)',
  },
  // TOTALS row = faint gold tint + gold top border (HANDOFF rule 6).
  // `border` is consumed as the row's borderTop by MobileDynastyTable.
  totals: {
    background: 'rgba(234, 179, 8, 0.08)',
    border: '1px solid rgba(234, 179, 8, 0.5)',
  },
  // Sticky-column opaque equivalents. even/odd are the exact same solids as the
  // scrolling rows above. `totals` is the opaque equivalent of the gold totals
  // tint (rgba(234,179,8,0.08)) composited over the page background (#111118).
  sticky: {
    even: '#181820',
    odd: '#0d0d14',
    totals: '#221e17',
    header: '#252530',
  },
  accrued: {
    rowBorder: '0.5px solid rgba(245, 158, 11, 0.8)',
  },
  // Borderless row variant (token-driven option on MobileDynastyTable) — no
  // per-row zebra: a flat sticky-safe row solid, a faint inter-row hairline, and
  // a slightly-shaded header. Used by the Stats-tab Career table; reusable.
  borderless: {
    row: '#101016',
    divider: '1px solid rgba(55, 65, 81, 0.26)',
    header: '#16161e',
  },
  // Live Scoring "your team" row highlight: gold tint + gold left border
  // (per the Live Scoring design — replaces the legacy brown #2a2520 port).
  userTeamRow: {
    background: 'rgba(234, 179, 8, 0.08)',
    borderLeft: '2px solid rgba(234, 179, 8, 0.6)',
  },
};

// ---------------------------------------------------------------------------
// 4. Modal Style
// ---------------------------------------------------------------------------
export const mobileModalStyle = {
  backdrop: 'rgba(0, 0, 0, 0.7)',
  container: {
    background: '#1a1a1e',
    border: '1px solid #444',
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
  },
};

// ---------------------------------------------------------------------------
// 5. Navigation Style
// ---------------------------------------------------------------------------
export const mobileNavStyle = {
  container: {
    background: 'linear-gradient(180deg, #252530 0%, #1c1c22 50%, #1a1a1e 100%)',
    borderBottom: '1px solid #444',
    boxShadow: 'inset 0 0 0 1px rgba(234, 179, 8, 0.12)',
  },
};

// ---------------------------------------------------------------------------
// 6. Color Palette (semantic tokens)
// ---------------------------------------------------------------------------
export const mobileColors = {
  // Backgrounds
  pageBg: '#111118',
  tileBgStart: '#252530',
  tileBgMid: '#1c1c22',
  tileBgEnd: '#1a1a1e',

  // Borders
  tileBorder: '#444',
  subtleBorder: '#2a2a30',

  // Primary accent (gold)
  primary: '#eab308',
  primaryGlow: 'rgba(234, 179, 8, 0.12)',
  primaryMuted: 'rgba(234, 179, 8, 0.15)',

  // Text hierarchy
  textPrimary: '#ffffff',
  textSecondary: '#f5f5f5',
  textTertiary: '#9ca3af',
  textMuted: '#6b7280',
  textDisabled: '#555',

  // Semantic colors
  success: '#4ade80',
  error: '#ef4444',
  warning: '#f59e0b',
  info: '#60a5fa',

  // Stat row type colors (for multi-line stat displays) — locked house treatment:
  // 14-day / rolling row = amber #f59e0b; accrued row = emerald #10b981.
  seasonText: '#ffffff',
  rollingText: '#f59e0b',
  accruedText: '#10b981',

  // Divider
  divider: '#2a2a30',
};

// ---------------------------------------------------------------------------
// 7. Text Styles
// ---------------------------------------------------------------------------
export const mobileTextStyles = {
  heading: { color: '#ffffff', fontWeight: 600 },
  subheading: { color: '#f5f5f5', fontWeight: 500 },
  body: { color: '#9ca3af' },
  muted: { color: '#6b7280' },
  accent: { color: '#eab308', fontWeight: 600 },
  // SHARED label/header TYPOGRAPHY (single source so weights can't drift per
  // component). Color is set by the consumer; these carry size/weight/transform.
  // Legible-not-heavy standard: ≤700 weight, ≥10px labels, ≥11px primary text.
  sectionHeader: { fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' },
  tabLabel: { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.2px' },
};

// ---------------------------------------------------------------------------
// Default export — aggregated theme
// ---------------------------------------------------------------------------
const mobileTheme = {
  page: mobilePageBackground,
  tile: mobileTileStyle,
  table: mobileTableStyles,
  modal: mobileModalStyle,
  nav: mobileNavStyle,
  colors: mobileColors,
  text: mobileTextStyles,
};

export default mobileTheme;
